import { Input, InputType, Label } from '../../components/primitives';
import { FormError } from '../../components/ui/FormError';
import { PasswordStrengthIndicator } from '../../components/ui/PasswordStrengthIndicator';
import { testId } from '../../utils';
import { PASSWORD_MIN } from '../../utils/constants';
import { FormElement } from '../account/FormElement';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Form from '@radix-ui/react-form';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const ChangePassword: React.FC<{ showLoader: boolean }> = ({
  showLoader,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.user.settings.account',
  });

  const ChangePasswordSchema = z.object({
    currentPassword: z.string().optional(),
    newPassword: z.string().min(PASSWORD_MIN),
  });
  type ChangePasswordSchema = z.infer<typeof ChangePasswordSchema>;

  const { handleSubmit, register, watch, reset, formState, setError } =
    useForm<ChangePasswordSchema>({
      resolver: zodResolver(ChangePasswordSchema),
      mode: 'onBlur',
      defaultValues: {
        currentPassword: '',
        newPassword: '',
      },
      values: {
        currentPassword: '',
        newPassword: '',
      },
    });

  const changePasswordMutation = Cornerstone.useChangePassword({
    mutation: {
      onError: (error: any) => {
        if (error?.response?.data?.errorCode === 'NOT_CURRENT_PASSWORD') {
          setError('currentPassword', {
            type: 'manual',
            message: t('incorrect_entry') as string,
          });
        }
        console.log({
          title: 'Failed to update account',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const resetForm = React.useCallback(() => {
    reset({
      currentPassword: '',
      newPassword: '',
    });
  }, [reset]);

  function onChangePassword(data: ChangePasswordSchema) {
    changePasswordMutation.mutate({
      data: {
        currentPassword: data.currentPassword ?? '',
        newPassword: data.newPassword,
      },
    });
    resetForm();
  }

  const currentPasswordError = formState.errors.currentPassword;
  const newPasswordError = formState.errors.newPassword;

  return (
    <FormElement
      onCancel={resetForm}
      label={t('password')}
      title={t('modal_title_password')}
      // bullets for value
      value={'\u2022'.repeat(8)}
      loading={showLoader}
      success={changePasswordMutation.isSuccess}
      onSubmit={handleSubmit(onChangePassword)}
      {...testId('password')}
    >
      <Form.Field name={'currentPassword'} className="space-y-1">
        <Form.Label>
          <Label {...testId('current-password', 'label')}>
            {t('current_password')}
          </Label>
        </Form.Label>
        <Form.Control asChild>
          <Input
            invalid={!!currentPasswordError}
            {...testId('current-password')}
            type={InputType.PASSWORD}
            {...register('currentPassword')}
          />
        </Form.Control>

        {currentPasswordError && (
          <FormError {...testId('password')}>
            {currentPasswordError.message}
          </FormError>
        )}
      </Form.Field>

      <Form.Field name={'newPassword'} className="space-y-1">
        <Form.Label>
          <Label {...testId('new-password', 'label')}>
            {t('new_password')}
          </Label>
        </Form.Label>
        <Form.Control asChild>
          <Input
            invalid={!!newPasswordError}
            {...testId('current-password')}
            type={InputType.PASSWORD}
            {...register('newPassword')}
          />
        </Form.Control>
        <PasswordStrengthIndicator password={watch('newPassword')} />
        {newPasswordError && (
          <FormError {...testId('newPassword')}>
            {newPasswordError.message}
          </FormError>
        )}
      </Form.Field>
    </FormElement>
  );
};
